<template>
  <ConfirmDialog ref="confirm"/>
  <LoadedCategoriesDialog
    ref="loadedCatDialog"
    v-bind:categories="tableModel.bind"
  />

  <v-container>
    <Card>
      <div class="cardTitle">{{ $t('Категории поставщиков') }}</div>
      <v-form v-model="valid">
        <DataTableVirtual
          :loading="loading"
          loading-text="Загрузка данных..."
          no-data-text="Нет данных"
          v-bind.sync="tableModel.bind"
          v-on="tableModel.on"
          :page.sync="tableModel.bind.options.page"
          class="no-table-border"
          disable-sort
          fixed-header
        >
          <template v-slot:item.name="{ item }">
            <TextArea
              v-if="item.isEditing"
              placeholder="Введите наименование"
              v-model.trim="item.name"
              :rules="[].concat(validation.required(), validation.text(255))"
              class="grid-fields"
            />
            <span v-else class="grid-fields">
              {{ item.name }}
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon v-if="!item.isEditing" icon="mdi-pencil" @click="updateCategory(item)"/>
            <v-icon icon="mdi-close" @click="deleteCategory(item)"/>
          </template>
        </DataTableVirtual>
      </v-form>
      
      <span v-if="tableModel.bind.totalEntries >= 100" style="color: rgba(var(--error-color))">{{ $t('Достигнуто максимальное количество категорий поставщиков') }}</span>
      <v-card-actions>
        <v-btn
          :disabled="tableModel.bind.totalEntries >= 100"
          prepend-icon="mdi-plus"
          color="primary"
          density="compact"
          variant="elevated"
          class="rounded"
          size="small"
          @click="createCategory"
        >{{ $t('Добавить') }}</v-btn>
        <v-btn
          :disabled="tableModel.bind.totalEntries <= 0"
          color="primary"
          density="compact"
          variant="outlined"
          class="ml-4 rounded"
          size="small"
          @click="deleteAllCategories"
        >{{ $t('Удалить все') }}</v-btn>
        <v-btn
          :disabled="!valid"
          color="primary"
          density="compact"
          variant="outlined"
          class="ml-4 rounded"
          size="small"
          @click="saveChanges"
        >{{ $t('Сохранить') }}</v-btn>
        <v-dialog>
          <template v-slot:activator="{ props: activatorProps }">
            <v-btn
              style="margin-left: auto !important;"
              color="primary"
              density="compact"
              variant="outlined"
              class="ml-4 rounded"
              size="small"
              v-bind="activatorProps"
            >{{ $t('Загрузить из Excel') }}</v-btn>
          </template>
          <template v-slot:default="{ isActive }">
            <FileInput
              style="width: 40%; align-self: center"
              title="Добавление из Excel"
              is-edit
              class="mb-3"
              :documents="documents"
              :patterns="patterns"
              :upload-file="(file) => onUpload(file) && (isActive.value = false)"
              :downloadPattern="excelCategoriesPattern"
              :uploadExtensions="availableDownloadTypes"
            >
              <template v-slot:file-icon="{ document }">
                <FileIcon :document="document" />
              </template>
            </FileInput>
          </template>
        </v-dialog>
      </v-card-actions>
      <Pagination
        :table-model="tableModel"
        :show-page-size-select="false"
      />
    </Card>
  </v-container>
</template>

<script>
import api from '@/api';
import { toast } from '@/lib/toast';
import RemoteTableModel from '@/components/RemoteTableModel';
import validation from '@/components/validation';
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import Card from '@intec/vue-components/form/Card.vue';
import TextArea from '@intec/vue-components/form/TextArea.vue';
import FileInput from "@intec/vue-components/form/FileInput.vue";
import LoadedCategoriesDialog from "@/components/categories/LoadedCategoriesDialog.vue";
import FileIcon from "@/components/FileIcon.vue";
import Pagination from '@/components/Pagination.vue';
import DataTableVirtual from "@intec/vue-components/table/DataTableVirtual.vue"

export default {
  name: 'Categories',
  components: {FileIcon, Card, ConfirmDialog, TextArea, FileInput, LoadedCategoriesDialog, Pagination, DataTableVirtual },
  data () {
    let tableModel = new RemoteTableModel({
      headers: [
        {
          title: 'Наименование',
          key: 'name',
          headerProps: {
            'data-required': 'true'
          }
        },
        {
          key: 'actions',
          width: '5%',
          align: 'end'
        }
      ],
      loader(query) {
        return api.get('/admin/categories', { query: JSON.stringify(query) });
      },
      options: {
        itemsPerPage: 10,
      },
    });
    return {
      loading: false,
      tableModel: tableModel,
      tempIdCounter: 0,
      updatedCategoryList: [],
      valid: true,
      validation,
      documents: [],
      isExportExcel: false,
      patterns: [{
        displayName: 'Скачать шаблон для заполнения',
        fileName: 'Шаблон_категории_закупки.xlsx',
        href: '/admin/categories/loadExcelPattern'
      }],
      availableDownloadTypes: [
        'application/vnd.ms-excel',
        'application/excel',
        'application/x-excel',
        'application/x-msexcel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ],
      maxFileMBSize: 20
    }
  },
  methods: {
    saveChanges() {
      this.saveCategories(false);
    },

    async saveCategories (isExcel) {
      if (!this.valid) {
        toast.error('Заполните все обязательные поля');
        return;
      }

      try {
        this.loading = true;
        const formData = new FormData();
        formData.append('data', JSON.stringify({ categories: this.updatedCategoryList, isExportExcel: this.isExportExcel }))
        await api.post(`/admin/categories/save`, formData);
        this.isExportExcel = false;
        this.updatedCategoryList = [];
        this.tableModel.reload();
        isExcel ? null : toast.info('Изменения сохранены!');
      } catch (e) {
        toast.error(e.message);
      } finally {
        this.loading = false;
      }
    },
    async deleteAllCategories() {
      const approve = await this.$refs.confirm.open(
        'Удаление всех категорий закупки',
        'Вы действительно хотите удалить все категории поставщиков?'
      );
      if (approve) {
        try {
          this.loading = true;
          await api.delete('/admin/categories/deleteAll');
          this.tableModel.reload();
          toast.info('Категории закупки успешно удалены!');
        } catch (e) {
          toast.error(e.message);
        } finally {
          this.loading = false;
        }
      }
    },
    async deleteCategory(item) {
      if (item._tempId) {
        const indexInTable = this.tableModel.bind.items.findIndex(category => category._tempId === item._tempId);
        const indexInUpdated = this.updatedCategoryList.findIndex(category => category._tempId === item._tempId);
        if (indexInTable !== -1 && indexInUpdated !== -1) {
          this.tableModel.bind.items.splice(indexInTable, 1);
          this.updatedCategoryList.splice(indexInUpdated, 1);
          this.valid = true;
        }
        return;
      }

      const approve = await this.$refs.confirm.open(
        'Удаление категории закупки',
        'Вы действительно хотите удалить категорию поставщика?'
      );
      if (approve) {
        try {
          this.loading = true;
          await api.delete(`/admin/categories/${item.id}/delete`);
          this.tableModel.reload();
          toast.info('Категория закупки удалена!');
        } catch (e) {
          toast.error(e.message);
        } finally {
          this.loading = false;
        }
      }
    },
    createCategory () {
      const newItem = {
        _tempId: `temp_${this.updatedCategoryList.length + 1}`,
        id: null,
        name: null,
        isEditing: true
      };
      this.tableModel.bind.items.push(newItem);
      this.updatedCategoryList.push(newItem);
    },
    updateCategory(item) {
      item.isEditing = true;
      this.updatedCategoryList.push(item);
    },
    async onUpload(file) {   
      if (file.size > this.maxFileBytesSize()) {
        toast.error(this.$t(`Ошибка: Файл: {_1}. Превышает максимальный размер {_2} Мбайт.`, {'_1': file.name, '_2': this.maxFileMBSize}));
        return;
      }
      
      try {
        this.loading = true;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('data', JSON.stringify({'categoriesUpdated': this.updatedCategoryList}));
        let resp = await api.post('/admin/categories/loadExcelCategories', formData);
        await this.$refs.loadedCatDialog.performCheck(this.tableModel.bind.items.concat(resp.categories));
        this.isExportExcel = this.$refs.loadedCatDialog.confirmationResult === 1 ? true : false;
        this.updatedCategoryList = this.tableModel.bind.items;
        await this.saveCategories(true);
        this.tableModel.reload();
      } catch (e) {
        toast.error(e.message);
      } finally {
        this.loading = false;
      }
    },
    maxFileBytesSize() {
      return this.maxFileMBSize * 1024 * 1024;
    },
    excelCategoriesPattern(pattern) {
      window.location.href = pattern.href;
    }
  },
}
</script>

<style scoped lang="scss">
:deep(.v-data-table) {
  th {
    background-color: rgb(var(--grey-lighten-color)) !important;
    .v-data-table-header__content {
      font-size: 16px;
      color: rgb(var(--black-color)) !important;
    }
  }
  [data-required] {
    span::after {
      content: ' *';
      color: rgba(var(--error-color));
    }
  }
  .v-table__wrapper > table > tbody > tr > td {
    padding: 0;
  }
}

.no-table-border {
  border: none;
  border-radius: 0;
}

.grid-fields {
  margin: 10px 5px;
}
</style>